import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Section = ({ title, hint, children, onSave, saving, testid }) => (
  <section className="card p-6 sm:p-8" data-testid={testid}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="font-serif text-2xl text-ink">{title}</h3>
        {hint && <p className="mt-1 max-w-lg text-xs leading-relaxed text-stone-500">{hint}</p>}
      </div>
      {onSave && (
        <button onClick={onSave} disabled={saving} data-testid={`${testid}-save`}
          className="btn-dark !px-5 !py-2.5 text-xs disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </button>
      )}
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

const GalleryManager = ({ title, hint, items, testidPrefix, uploading, onUpload, onChange }) => {
  const inputRef = useRef(null);
  const move = (from, to) => {
    const g = [...items];
    const [x] = g.splice(from, 1);
    g.splice(to, 0, x);
    onChange(g);
  };
  return (
    <div className="mb-6 rounded-2xl bg-bone2 p-5 ring-1 ring-black/5 sm:p-6" data-testid={`${testidPrefix}-manager`}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <p className="font-serif text-xl text-ink">{title}</p>
        <div className="flex items-center gap-4">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
            onChange={onUpload} data-testid={`${testidPrefix}-upload-input`} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid={`${testidPrefix}-upload-btn`}
            className="flex items-center gap-1.5 text-xs font-semibold text-ink hover:underline disabled:opacity-50">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Upload photo
          </button>
          <button type="button" data-testid={`${testidPrefix}-add`}
            onClick={() => onChange([...items, { url: "", caption: "", alt: "" }])}
            className="flex items-center gap-1.5 text-xs font-semibold text-ink hover:underline">
            <Plus className="h-3.5 w-3.5" /> Add by URL
          </button>
        </div>
      </div>
      <p className="mb-3 text-[11px] leading-relaxed text-stone-400">{hint}</p>
      {items.map((g, i) => (
        <div key={i} className="mb-4 rounded-xl bg-white p-4 ring-1 ring-black/5" data-testid={`${testidPrefix}-item-${i}`}>
          <div className="flex items-start gap-3">
            {g.url && <img src={g.url} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover ring-1 ring-black/10" />}
            <div className="grid flex-1 gap-2">
              <input value={g.url} data-testid={`${testidPrefix}-url-${i}`} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                className="field !py-2 text-xs" placeholder="https://…image.jpg" />
              <div className="grid grid-cols-2 gap-2">
                <input value={g.caption} data-testid={`${testidPrefix}-caption-${i}`} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))}
                  className="field !py-2 text-xs" placeholder="Caption (shown under the photo)" />
                <input value={g.alt} data-testid={`${testidPrefix}-alt-${i}`} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, alt: e.target.value } : x))}
                  className="field !py-2 text-xs" placeholder="Alt text (for screen readers)" />
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-1">
              <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}
                className="rounded-lg border border-black/10 px-2 py-1 text-xs disabled:opacity-30">↑</button>
              <button type="button" aria-label="Move down" disabled={i === items.length - 1} onClick={() => move(i, i + 1)}
                className="rounded-lg border border-black/10 px-2 py-1 text-xs disabled:opacity-30">↓</button>
              <button type="button" aria-label="Remove" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700">✕</button>
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-stone-400">No photos yet — upload one or add by URL.</p>}
    </div>
  );
};

export default function SettingsTab({ user }) {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const contentQ = useQuery({ queryKey: ["admin-content"], queryFn: () => api.get("/admin/content").then((r) => r.data) });

  const [pricing, setPricing] = useState(null);
  const [windows, setWindows] = useState(null);
  const [contact, setContact] = useState(null);
  const [content, setContent] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState("");
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    if (settingsQ.data && !pricing) {
      const d = settingsQ.data;
      setPricing({ hourly_rate: d.hourly_rate, min_duration_hours: d.min_duration_hours, buffer_minutes: d.buffer_minutes ?? 30, show_pricing: d.show_pricing, guest_threshold: d.guest_threshold, capacity: d.capacity });
      setContact({ venue_name: d.venue_name, tagline: d.tagline, contact_phone: d.contact_phone, contact_email: d.contact_email, address: d.address });
      setWindows({ rental_windows: d.rental_windows || [], exceptions: d.exceptions || [] });
    }
  }, [settingsQ.data, pricing]);

  useEffect(() => {
    if (contentQ.data && !content) {
      setContent({ description: contentQ.data.description || "", amenities: (contentQ.data.amenities || []).join("\n"), faqs: contentQ.data.faqs || [], gallery: contentQ.data.gallery || [], charcuterie_gallery: contentQ.data.charcuterie_gallery || [] });
    }
  }, [contentQ.data, content]);

  const saveSettings = async (key, payload) => {
    setSaving(key);
    try {
      await api.put("/admin/settings", payload);
      await qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["content"] });
      toast.success("Saved.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const saveContent = async () => {
    setSaving("content");
    try {
      await api.put("/admin/content", {
        description: content.description,
        amenities: content.amenities.split("\n").map((a) => a.trim()).filter(Boolean),
        faqs: content.faqs, gallery: content.gallery, charcuterie_gallery: content.charcuterie_gallery,
      });
      await qc.invalidateQueries({ queryKey: ["admin-content"] });
      qc.invalidateQueries({ queryKey: ["content"] });
      toast.success("Content saved.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setSaving("pw");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const uploadImage = async (e, key) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/images", fd);
      setContent((c) => ({ ...c, [key]: [...(c[key] || []), { url: r.data.url, caption: "", alt: "" }] }));
      toast.success("Photo uploaded — add a caption, then hit Save.");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed."));
    } finally {
      setUploadingKey(null);
    }
  };

  if (!pricing || !windows || !contact || !content) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;
  }

  const windowFor = (dow) => windows.rental_windows.find((w) => w.dow === dow);
  const toggleWindow = (dow, on) => {
    setWindows((w) => ({
      ...w,
      rental_windows: on
        ? [...w.rental_windows.filter((x) => x.dow !== dow), { dow, start: "10:00", end: "22:00" }]
        : w.rental_windows.filter((x) => x.dow !== dow),
    }));
  };
  const setWindowTime = (dow, key, val) => {
    setWindows((w) => ({ ...w, rental_windows: w.rental_windows.map((x) => (x.dow === dow ? { ...x, [key]: val } : x)) }));
  };

  return (
    <div className="grid items-start gap-6 lg:grid-cols-2" data-testid="admin-settings-tab">
      <div className="space-y-6">
        <Section title="Pricing & capacity" testid="admin-pricing" saving={saving === "pricing"}
          hint="Shown publicly next to venue details. Turn off “Show pricing” to hide the rate without deleting it."
          onSave={() => saveSettings("pricing", { ...pricing, hourly_rate: Number(pricing.hourly_rate), min_duration_hours: Number(pricing.min_duration_hours), buffer_minutes: Number(pricing.buffer_minutes), guest_threshold: Number(pricing.guest_threshold), capacity: Number(pricing.capacity) })}>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label" htmlFor="set-rate">Hourly rate ($)</label>
              <input id="set-rate" type="number" min="0" value={pricing.hourly_rate} data-testid="admin-rate-input"
                onChange={(e) => setPricing({ ...pricing, hourly_rate: e.target.value })} className="field" /></div>
            <div><label className="label" htmlFor="set-min">Minimum hours</label>
              <input id="set-min" type="number" min="1" value={pricing.min_duration_hours} data-testid="admin-min-hours-input"
                onChange={(e) => setPricing({ ...pricing, min_duration_hours: e.target.value })} className="field" /></div>
            <div><label className="label" htmlFor="set-cap">Capacity (guests)</label>
              <input id="set-cap" type="number" min="1" value={pricing.capacity} data-testid="admin-capacity-input"
                onChange={(e) => setPricing({ ...pricing, capacity: e.target.value })} className="field" /></div>
            <div><label className="label" htmlFor="set-threshold">Guest-count warning at</label>
              <input id="set-threshold" type="number" min="1" value={pricing.guest_threshold} data-testid="admin-threshold-input"
                onChange={(e) => setPricing({ ...pricing, guest_threshold: e.target.value })} className="field" /></div>
            <div><label className="label" htmlFor="set-buffer">Setup buffer per booking (min)</label>
              <input id="set-buffer" type="number" min="0" step="15" value={pricing.buffer_minutes} data-testid="admin-buffer-input"
                onChange={(e) => setPricing({ ...pricing, buffer_minutes: e.target.value })} className="field" /></div>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-stone-400">
            The buffer is blocked before and after every event for setup and cleanup. You can extend it per
            booking in the Calendar tab when a client purchases extra time.
          </p>
          <label className="mt-5 flex cursor-pointer items-center gap-3 text-sm text-stone-700">
            <input type="checkbox" checked={!!pricing.show_pricing} data-testid="admin-show-pricing-toggle"
              onChange={(e) => setPricing({ ...pricing, show_pricing: e.target.checked })} className="h-4 w-4 rounded accent-[#1C1917]" />
            Show pricing publicly
          </label>
        </Section>

        <Section title="Rental hours" testid="admin-windows" saving={saving === "windows"}
          hint="Weekly windows visitors can book within. Unchecked days show as closed on the public calendar."
          onSave={() => saveSettings("windows", { rental_windows: windows.rental_windows, exceptions: windows.exceptions })}>
          <div className="space-y-2.5">
            {DAYS.map((label, dow) => {
              const w = windowFor(dow);
              return (
                <div key={dow} className="flex items-center gap-3 rounded-xl bg-bone2 px-4 py-2.5">
                  <label className="flex w-24 cursor-pointer items-center gap-2 text-sm font-medium text-stone-700">
                    <input type="checkbox" checked={!!w} onChange={(e) => toggleWindow(dow, e.target.checked)}
                      data-testid={`admin-window-${label.toLowerCase()}`} className="h-4 w-4 rounded accent-[#1C1917]" />
                    {label}
                  </label>
                  {w ? (
                    <div className="flex items-center gap-2 text-sm">
                      <input type="time" value={w.start} onChange={(e) => setWindowTime(dow, "start", e.target.value)}
                        aria-label={`${label} start`} className="field !w-auto !px-2.5 !py-1.5" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={w.end} onChange={(e) => setWindowTime(dow, "end", e.target.value)}
                        aria-label={`${label} end`} className="field !w-auto !px-2.5 !py-1.5" />
                    </div>
                  ) : <span className="text-xs text-stone-400">Closed for rentals</span>}
                </div>
              );
            })}
          </div>
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="label !mb-0">Date exceptions</p>
              <button type="button" data-testid="admin-exception-add"
                onClick={() => setWindows((w) => ({ ...w, exceptions: [...w.exceptions, { date: "", closed: true, start: "10:00", end: "22:00" }] }))}
                className="flex items-center gap-1.5 text-xs font-semibold text-ink hover:underline">
                <Plus className="h-3.5 w-3.5" /> Add exception
              </button>
            </div>
            {windows.exceptions.length === 0 && <p className="text-xs text-stone-400">No exceptions — e.g. closed for a holiday, or special hours on one date.</p>}
            <div className="space-y-2.5">
              {windows.exceptions.map((ex, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl bg-bone2 px-4 py-2.5 text-sm">
                  <input type="date" value={ex.date} aria-label="Exception date"
                    onChange={(e) => setWindows((w) => ({ ...w, exceptions: w.exceptions.map((x, j) => j === i ? { ...x, date: e.target.value } : x) }))}
                    className="field !w-auto !px-2.5 !py-1.5" />
                  <label className="flex items-center gap-2 text-xs text-stone-600">
                    <input type="checkbox" checked={!!ex.closed}
                      onChange={(e) => setWindows((w) => ({ ...w, exceptions: w.exceptions.map((x, j) => j === i ? { ...x, closed: e.target.checked } : x) }))}
                      className="h-4 w-4 rounded accent-[#1C1917]" /> Closed
                  </label>
                  {!ex.closed && (
                    <>
                      <input type="time" value={ex.start} aria-label="Exception start"
                        onChange={(e) => setWindows((w) => ({ ...w, exceptions: w.exceptions.map((x, j) => j === i ? { ...x, start: e.target.value } : x) }))}
                        className="field !w-auto !px-2.5 !py-1.5" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={ex.end} aria-label="Exception end"
                        onChange={(e) => setWindows((w) => ({ ...w, exceptions: w.exceptions.map((x, j) => j === i ? { ...x, end: e.target.value } : x) }))}
                        className="field !w-auto !px-2.5 !py-1.5" />
                    </>
                  )}
                  <button type="button" aria-label="Remove exception"
                    onClick={() => setWindows((w) => ({ ...w, exceptions: w.exceptions.filter((_, j) => j !== i) }))}
                    className="ml-auto rounded-full p-1.5 text-stone-400 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Venue & contact info" testid="admin-contact" saving={saving === "contact"}
          hint="Appears on the FAQs & Contact page and in the footer."
          onSave={() => saveSettings("contact", contact)}>
          <div className="grid gap-4 sm:grid-cols-2">
            {[["venue_name", "Venue name"], ["tagline", "Tagline"], ["contact_phone", "Phone"], ["contact_email", "Email"], ["address", "Address"]].map(([k, label]) => (
              <div key={k} className={k === "address" || k === "tagline" ? "sm:col-span-2" : ""}>
                <label className="label" htmlFor={`set-${k}`}>{label}</label>
                <input id={`set-${k}`} value={contact[k] || ""} data-testid={`admin-contact-${k}`}
                  onChange={(e) => setContact({ ...contact, [k]: e.target.value })} className="field" />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Your password" testid="admin-password" saving={saving === "pw"} onSave={null}>
          <form onSubmit={changePassword} className="space-y-4">
            <p className="text-xs text-stone-500">Signed in as <span className="font-semibold text-stone-700">{user.email}</span>. Each admin changes their own password here.</p>
            <div><label className="label" htmlFor="pw-current">Current password</label>
              <input id="pw-current" type="password" required value={pw.current_password} autoComplete="current-password"
                data-testid="admin-pw-current" onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="field" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label" htmlFor="pw-new">New password</label>
                <input id="pw-new" type="password" required minLength={8} value={pw.new_password} autoComplete="new-password"
                  data-testid="admin-pw-new" onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="field" /></div>
              <div><label className="label" htmlFor="pw-confirm">Confirm new</label>
                <input id="pw-confirm" type="password" required value={pw.confirm} autoComplete="new-password"
                  data-testid="admin-pw-confirm" onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="field" /></div>
            </div>
            <button type="submit" disabled={saving === "pw"} data-testid="admin-pw-save"
              className="btn-dark !px-5 !py-2.5 text-xs disabled:opacity-60">
              {saving === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </button>
          </form>
        </Section>
      </div>

      <div className="space-y-6">
        <Section title="Page content" testid="admin-content" saving={saving === "content"} onSave={saveContent}
          hint="Edits appear on the public site as soon as you save.">
          <div className="space-y-6">
            <div>
              <label className="label" htmlFor="content-desc">Venue description</label>
              <textarea id="content-desc" rows={4} value={content.description} data-testid="admin-content-description"
                onChange={(e) => setContent({ ...content, description: e.target.value })} className="field" />
            </div>
            <div>
              <label className="label" htmlFor="content-amenities">What's included (one per line)</label>
              <textarea id="content-amenities" rows={6} value={content.amenities} data-testid="admin-content-amenities"
                onChange={(e) => setContent({ ...content, amenities: e.target.value })} className="field font-mono text-xs" />
            </div>
            <div>
              <div className="mb-3 flex items-center justify-between">
                <p className="label !mb-0">FAQs</p>
                <button type="button" data-testid="admin-faq-add"
                  onClick={() => setContent({ ...content, faqs: [...content.faqs, { q: "", a: "" }] })}
                  className="flex items-center gap-1.5 text-xs font-semibold text-ink hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add FAQ
                </button>
              </div>
              <div className="space-y-3">
                {content.faqs.map((f, i) => (
                  <div key={i} className="rounded-xl bg-bone2 p-4">
                    <div className="flex items-start gap-2">
                      <input value={f.q} placeholder="Question" aria-label={`FAQ ${i + 1} question`}
                        onChange={(e) => setContent({ ...content, faqs: content.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x) })}
                        className="field !bg-white" />
                      <button type="button" aria-label={`Remove FAQ ${i + 1}`}
                        onClick={() => setContent({ ...content, faqs: content.faqs.filter((_, j) => j !== i) })}
                        className="mt-2 rounded-full p-1.5 text-stone-400 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <textarea rows={2} value={f.a} placeholder="Answer" aria-label={`FAQ ${i + 1} answer`}
                      onChange={(e) => setContent({ ...content, faqs: content.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x) })}
                      className="field mt-2 !bg-white" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="label !mb-1">Photo galleries</p>
              <p className="mb-4 text-[11px] leading-relaxed text-stone-400">
                Two photo piles power the site — venue events and charcuterie setups. Upload from your phone or computer (or paste URLs), reorder with the arrows, then hit Save.
              </p>
              <GalleryManager
                title="Venue event photos"
                testidPrefix="admin-venue-gallery"
                hint="Shown in the venue photo pile on the homepage and the Space page. The first photo doubles as the homepage hero."
                items={content.gallery || []}
                uploading={uploadingKey === "gallery"}
                onUpload={(e) => uploadImage(e, "gallery")}
                onChange={(items) => setContent((c) => ({ ...c, gallery: items }))}
              />
              <GalleryManager
                title="Charcuterie setup photos"
                testidPrefix="admin-charcuterie-gallery"
                hint="Shown in the charcuterie photo pile on the homepage, under the 'we come to you' section. Add new setups as you style them."
                items={content.charcuterie_gallery || []}
                uploading={uploadingKey === "charcuterie_gallery"}
                onUpload={(e) => uploadImage(e, "charcuterie_gallery")}
                onChange={(items) => setContent((c) => ({ ...c, charcuterie_gallery: items }))}
              />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
